import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { PurchaseOrder, PurchaseOrderItem, PurchaseOrderRecommendation } from '@domain/purchasing';


export const usePurchasing = (id?: string, filters?: { draft?: boolean }) => {
  const queryClient = useQueryClient();

  const { data: purchaseOrders, isLoading, error } = useQuery({
    queryKey: ['purchaseOrders', filters], // Add filters to queryKey to trigger reload when they change
    queryFn: () => container.purchasingRepository.getPurchaseOrders(filters),
    staleTime: 0, // Always consider data stale to ensure fresh data
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    enabled: !id
  },);

  const { data: purchaseOrder, isLoading: isLoadingPurchaseOrder, error: purchaseOrderError } = useQuery({
    queryKey: ['purchaseOrder', id],
    queryFn: () => container.purchasingRepository.getPurchaseOrder(id!),
    staleTime: 0, // Always consider data stale to ensure fresh data
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    enabled: !!id
  });

  const createPurchaseOrder = useMutation({
    mutationFn: (purchaseOrder: PurchaseOrder) =>
      container.purchasingRepository.createPurchaseOrder(purchaseOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItemsGrouped'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    }
  });

  const updatePurchaseOrder = useMutation({
    mutationFn: async ({ id, purchaseOrder }: { id: string, purchaseOrder: Partial<PurchaseOrder> }) => {
      const result = await container.purchasingRepository.updatePurchaseOrder(id, purchaseOrder);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItemsGrouped'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder', variables.id] });
    }
  });

  const publishPurchaseOrder = useMutation({
    mutationFn: async (id: string) => {
      const result = await container.purchasingRepository.publishPurchaseOrder(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItemsGrouped'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder', id] });
    }
  });

  const deletePurchaseOrder = useMutation({
    mutationFn: async (id: string) => {
      const result = await container.purchasingRepository.deletePurchaseOrder(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_data, id) => {
      // Invalidate the relevant queries
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder', id] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItemsGrouped'] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItems'] });
    },
  });

  const calculateAmounts = async (purchaseOrderId: string): Promise<PurchaseOrderRecommendation[]> => {
    const result = await container.purchasingRepository.calculateAmounts(purchaseOrderId);
    if (result.status === 'error') {
      throw new Error(result.error.message);
    }
    return result.data;
  };

  const createPurchaseOrderItem = useMutation({
    mutationFn: async ({ purchaseOrderId, item }: { purchaseOrderId: string, item: Omit<PurchaseOrderItem, 'id'> }) => {
      const result = await container.purchasingRepository.createPurchaseOrderItem(purchaseOrderId, item);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { purchaseOrderId }) => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrder', purchaseOrderId] });
      queryClient.invalidateQueries({ queryKey: ['purchaseOrderItemsGrouped'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    }
  });

  return {
    purchaseOrders: purchaseOrders || [],
    purchaseOrder,
    isLoading,
    isLoadingPurchaseOrder,

    createPurchaseOrder: createPurchaseOrder.mutateAsync,
    isCreating: createPurchaseOrder.isPending,
    updatePurchaseOrder: updatePurchaseOrder.mutateAsync,
    isUpdating: updatePurchaseOrder.isPending,
    publishPurchaseOrder: publishPurchaseOrder.mutateAsync,
    isPublishing: publishPurchaseOrder.isPending,
    deletePurchaseOrder: deletePurchaseOrder.mutateAsync,
    isDeleting: deletePurchaseOrder.isPending,
    createPurchaseOrderItem: createPurchaseOrderItem.mutateAsync,
    isCreatingPurchaseOrderItem: createPurchaseOrderItem.isPending,

    calculateAmounts,

    purchaseOrderError,
    error
  };
}; 